import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { t } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Loader2, Sparkles, Rocket, Check } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const SECTORS = [
  "Tech / SaaS", "Restauration", "Commerce", "Beauté & Bien-être", "Santé",
  "Services aux entreprises", "Grandes entreprises", "Finance & Assurance",
  "Immobilier", "Automobile", "Artisanat & Bâtiment", "Éducation",
  "Sport & Loisirs", "Hôtellerie & Tourisme", "Logistique", "Industrie",
  "Design & Communication", "Agroalimentaire", "Énergie", "Juridique", "Autre",
];

const SIZES = ["1-10", "11-50", "51-200", "201-500", "500+"];

export default function CompanyCreate() {
  const { user, lang, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "", sector: "", description: "", address: "", city: "", country: "France",
    lat: null, lng: null, website: "", email: "", phone: "", size: "1-10",
    hosts_interns: false, typical_needs: "",
  });
  const [loading, setLoading] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [geocoded, setGeocoded] = useState(false);

  useEffect(() => { if (authLoading) return; if (!user) nav("/login"); }, [user, nav, authLoading]);

  // Auto-geocode via OpenStreetMap Nominatim (free, no key needed)
  const geocode = async () => {
    if (!form.address.trim() || !form.city.trim()) {
      toast.error(lang === "fr" ? "Renseignez l'adresse et la ville d'abord" : "Enter address and city first");
      return null;
    }
    setGeocoding(true);
    try {
      const q = encodeURIComponent(`${form.address}, ${form.city}, ${form.country}`);
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${q}`, {
        headers: { "Accept-Language": lang === "fr" ? "fr" : "en" },
      });
      const data = await r.json();
      if (!data.length) {
        toast.error(lang === "fr" ? "Adresse introuvable — vérifiez l'orthographe" : "Address not found — check spelling");
        return null;
      }
      const { lat, lon } = data[0];
      const coords = { lat: parseFloat(lat), lng: parseFloat(lon) };
      setForm((f) => ({ ...f, ...coords }));
      setGeocoded(true);
      toast.success(lang === "fr" ? "Adresse localisée ✓" : "Address located ✓");
      return coords;
    } catch {
      toast.error(lang === "fr" ? "Géocodage indisponible" : "Geocoding unavailable");
      return null;
    } finally {
      setGeocoding(false);
    }
  };

  const canGoNext = () => {
    if (step === 1) return form.name.trim() && form.sector && form.description.trim();
    if (step === 2) return form.address.trim() && form.city.trim() && form.country.trim();
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    let coords = { lat: form.lat, lng: form.lng };
    if (form.lat == null || form.lng == null) {
      coords = await geocode();
      if (!coords) return;
    }
    setLoading(true);
    try {
      await http.post("/companies", {
        ...form,
        lat: coords.lat,
        lng: coords.lng,
        typical_needs: (form.typical_needs || "").split(",").map((s) => s.trim()).filter(Boolean),
        tags: [],
      });
      toast.success(lang === "fr" ? "🎉 Votre vitrine est en ligne !" : "🎉 Your page is live!");
      nav("/company");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const Stepper = () => (
    <div className="flex items-center gap-2 mb-8">
      {[1, 2, 3].map((n) => (
        <div key={n} className="flex items-center gap-2 flex-1">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
            step >= n ? "bg-[#1E3A8A] text-white" : "bg-slate-200 text-slate-500"
          }`}>
            {step > n ? <Check className="w-4 h-4" /> : n}
          </div>
          {n < 3 && <div className={`h-0.5 flex-1 rounded ${step > n ? "bg-[#1E3A8A]" : "bg-slate-200"}`} />}
        </div>
      ))}
    </div>
  );

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 mb-3">
          <Rocket className="w-5 h-5 text-[#F97316]" />
          <span className="text-xs uppercase tracking-[0.2em] text-slate-500">
            {lang === "fr" ? "Onboarding · 3 étapes · 3 min" : "Onboarding · 3 steps · 3 min"}
          </span>
        </div>
        <h1 data-testid="cc-title" className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1E3A8A]">
          {lang === "fr" ? "Créer votre vitrine PME" : "Create your business page"}
        </h1>
        <p className="text-slate-600 mt-2 text-sm">
          {lang === "fr"
            ? "Visible sur la carte mondiale Proxijob en moins de 3 minutes."
            : "Live on the global Proxijob map in under 3 minutes."}
        </p>

        <div className="mt-8 rounded-3xl bg-white border border-slate-200 p-6 sm:p-8">
          <Stepper />

          <form onSubmit={submit} className="space-y-5">
            {step === 1 && (
              <div className="space-y-5 fade-up" data-testid="cc-step-1">
                <div>
                  <Label>{lang === "fr" ? "Nom de l'entreprise *" : "Business name *"}</Label>
                  <Input data-testid="cc-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" placeholder="Ex : Café des Artistes" />
                </div>
                <div>
                  <Label>{lang === "fr" ? "Secteur *" : "Sector *"}</Label>
                  <Select value={form.sector} onValueChange={(v) => setForm({ ...form, sector: v })}>
                    <SelectTrigger data-testid="cc-sector" className="mt-1.5"><SelectValue placeholder={lang === "fr" ? "Choisir un secteur" : "Choose a sector"} /></SelectTrigger>
                    <SelectContent>{SECTORS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{lang === "fr" ? "Présentation courte *" : "Short pitch *"}</Label>
                  <Textarea data-testid="cc-desc" rows={3} required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" placeholder={lang === "fr" ? "Ce que vous faites en 2 phrases, ce qui vous rend unique." : "What you do in 2 sentences, what makes you unique."} maxLength={280} />
                  <p className="text-xs text-slate-400 mt-1">{form.description.length}/280</p>
                </div>
                <div>
                  <Label>{lang === "fr" ? "Taille" : "Team size"}</Label>
                  <Select value={form.size} onValueChange={(v) => setForm({ ...form, size: v })}>
                    <SelectTrigger data-testid="cc-size" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5 fade-up" data-testid="cc-step-2">
                <div>
                  <Label>{lang === "fr" ? "Adresse *" : "Address *"}</Label>
                  <Input data-testid="cc-address" required value={form.address} onChange={(e) => { setForm({ ...form, address: e.target.value }); setGeocoded(false); }} className="mt-1.5" placeholder="12 rue des Lilas" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label>{lang === "fr" ? "Ville *" : "City *"}</Label>
                    <Input data-testid="cc-city" required value={form.city} onChange={(e) => { setForm({ ...form, city: e.target.value }); setGeocoded(false); }} className="mt-1.5" placeholder="Paris" />
                  </div>
                  <div>
                    <Label>{lang === "fr" ? "Pays *" : "Country *"}</Label>
                    <Input data-testid="cc-country" required value={form.country} onChange={(e) => { setForm({ ...form, country: e.target.value }); setGeocoded(false); }} className="mt-1.5" />
                  </div>
                </div>
                <div className={`rounded-2xl p-4 border transition-colors ${geocoded ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold flex items-center gap-1.5">
                        <MapPin className={`w-4 h-4 ${geocoded ? "text-emerald-600" : "text-slate-500"}`} />
                        {lang === "fr" ? "Localisation sur la carte" : "Map location"}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {geocoded
                          ? (lang === "fr" ? `📍 ${form.lat?.toFixed(4)}, ${form.lng?.toFixed(4)}` : `📍 ${form.lat?.toFixed(4)}, ${form.lng?.toFixed(4)}`)
                          : (lang === "fr" ? "Nous détectons automatiquement les coordonnées GPS depuis l'adresse." : "We auto-detect GPS coordinates from the address.")}
                      </p>
                    </div>
                    <Button
                      type="button"
                      data-testid="cc-geocode-btn"
                      onClick={geocode}
                      disabled={geocoding || !form.address || !form.city}
                      variant="outline"
                      className="rounded-full h-9 text-xs border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white shrink-0"
                    >
                      {geocoding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                        geocoded ? <Check className="w-3.5 h-3.5 mr-1" /> : <MapPin className="w-3.5 h-3.5 mr-1" />}
                      {geocoding ? (lang === "fr" ? "…" : "…") : geocoded ? (lang === "fr" ? "Localisé" : "Located") : (lang === "fr" ? "Localiser" : "Locate")}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-5 fade-up" data-testid="cc-step-3">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>{lang === "fr" ? "Site web" : "Website"}</Label><Input data-testid="cc-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1.5" placeholder="https://…" /></div>
                  <div><Label>{lang === "fr" ? "Email de contact" : "Contact email"}</Label><Input data-testid="cc-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" placeholder="contact@…" /></div>
                </div>
                <div><Label>{lang === "fr" ? "Téléphone" : "Phone"}</Label><Input data-testid="cc-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
                <div>
                  <Label>{lang === "fr" ? "Postes que vous recrutez souvent" : "Roles you often hire"}</Label>
                  <Input data-testid="cc-needs" value={form.typical_needs} onChange={(e) => setForm({ ...form, typical_needs: e.target.value })} className="mt-1.5" placeholder={lang === "fr" ? "Serveur, Cuisinier, Stagiaire (séparés par virgules)" : "Server, Cook, Intern (comma-separated)"} />
                  <p className="text-xs text-slate-500 mt-1">{lang === "fr" ? "L'IA utilise cette liste pour matcher les candidats." : "Our AI uses this list to match candidates."}</p>
                </div>
                <div className="flex items-center justify-between py-2 rounded-2xl bg-[#FFF7ED] px-4 border border-[#F97316]/30">
                  <div>
                    <Label className="cursor-pointer text-[#C2410C] font-semibold">
                      {lang === "fr" ? "J'accueille des stagiaires / jobs étudiants" : "I welcome interns / student jobs"}
                    </Label>
                    <p className="text-xs text-slate-500 mt-0.5">{lang === "fr" ? "Boost votre visibilité auprès des jeunes." : "Boosts visibility for young talent."}</p>
                  </div>
                  <Switch data-testid="cc-interns" checked={form.hosts_interns} onCheckedChange={(v) => setForm({ ...form, hosts_interns: v })} />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-6">
              <Button
                type="button"
                data-testid="cc-back-btn"
                variant="ghost"
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                disabled={step === 1}
                className="rounded-full text-slate-500"
              >
                ← {lang === "fr" ? "Retour" : "Back"}
              </Button>
              {step < 3 ? (
                <Button
                  type="button"
                  data-testid="cc-next-btn"
                  onClick={() => canGoNext() && setStep((s) => s + 1)}
                  disabled={!canGoNext()}
                  className="bg-[#1E3A8A] hover:bg-[#172554] rounded-full px-6"
                >
                  {lang === "fr" ? "Continuer" : "Continue"} <Sparkles className="w-4 h-4 ml-1.5" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  data-testid="cc-submit"
                  disabled={loading}
                  className="bg-[#F97316] hover:bg-[#EA580C] rounded-full px-6 text-white"
                >
                  {loading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> {lang === "fr" ? "Publication…" : "Publishing…"}</>
                    : <>🚀 {lang === "fr" ? "Publier ma vitrine" : "Publish my page"}</>}
                </Button>
              )}
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
}
